import { messages, providers } from '../constants';
import type { TopicMetadata } from '../types';

/** Display label for a library (E179) year node — UI only; never use for API matching. */
export function libraryYearDisplayName(year: {
    e179_Number?: number | null;
    e179_Name?: string | null;
}): string {
    if (year?.e179_Number != null && year.e179_Number !== undefined) {
        return `Year ${year.e179_Number}`;
    }
    const name = year?.e179_Name?.trim();
    return name || '';
}

function yearIdString(raw: unknown): string | null {
    if (raw == null) return null;
    const s = String(raw).trim();
    return s || null;
}

export const getSelectedProviderName = (selectedProvider: string): string => {
    const provider = providers.find(p => p.id === selectedProvider);
    return provider ? provider.name : messages.providersFallback;
};

export const getProviderName = (providerKey: string | null | undefined): string => {
    if (!providerKey) return '';
    const provider = providers.find(p => p.id === providerKey);
    return provider ? provider.name : providerKey;
};

/**
 * Branch prefix for provider-library tree nodes (must match server BuiltInCurriculumLibraryNodeId).
 */
export function librarySubjectBranchIdFromE179(subject: {
    e179_SubjectId?: string;
}): string {
    const sid = (subject.e179_SubjectId || '').trim();
    if (!sid) {
        throw new Error('e179_SubjectId is required for library curriculum tree nodes');
    }
    return `s-${sid}`;
}

/** Student curriculum subject branch prefix (StudentSubject entity id on the wire as e180_SubjectId). */
/** Stable key stage + year placement from library topic metadata (E313 ids). */
export type CurriculumPlacementKey = {
  keyStageId: string;
  yearId: string;
  /** Library/catalog subject display name — matches student E180 subject line. */
  subjectName?: string;
};

function normalizeGuidForCompare(id: string | null | undefined): string {
  return (id != null ? String(id).trim() : '').toLowerCase();
}

function normalizeSubjectNameForCompare(name: string | null | undefined): string {
  return (name != null ? String(name).trim() : '').toLowerCase();
}

function placementMatchKey(keyStageId: string, yearId: string): string {
  return `${normalizeGuidForCompare(keyStageId)}|${normalizeGuidForCompare(yearId)}`;
}

/** Unique KS/year placements for a set of library topics (for expanding E180 after add). */
export function collectPlacementKeysFromTopicMetadata(
  topics: any[],
  extractTopicMetadata: (topic: any) => TopicMetadata
): CurriculumPlacementKey[] {
  const seen = new Set<string>();
  const out: CurriculumPlacementKey[] = [];
  for (const topic of topics) {
    const m = extractTopicMetadata(topic);
    const keyStageId = (m.keyStageId || '').trim();
    const yearId = (m.yearId || '').trim();
    if (!keyStageId || !yearId) continue;
    const key = `${keyStageId}|${yearId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ keyStageId, yearId });
  }
  return out;
}

/**
 * Node ids for StudentCurriculumTreeView expandedNodes after Add Topics: open each affected
 * subject and key stage so year rows (with topic counts) are visible. Does not expand years
 * (topic lists stay lazy-loaded only when the user expands a year manually).
 */
export function computeStudentCurriculumExpandNodesForPlacements(
  studentCurriculumData: any,
  placements: CurriculumPlacementKey[]
): Set<string> {
  const expand = new Set<string>();
  const subjects = studentCurriculumData?.e180_Subjects;
  if (!subjects?.length || !placements.length) return expand;

  for (const subject of subjects) {
    let branchId: string;
    try {
      branchId = studentSubjectBranchIdFromE180(subject);
    } catch {
      continue;
    }
    const subjectLabel = normalizeSubjectNameForCompare(subject.e180_Name);
    const keyStages = subject.e180_KeyStages ?? [];
    let subjectExpanded = false;

    for (let ki = 0; ki < keyStages.length; ki++) {
      const ks = keyStages[ki];
      const ksId = ks.e180_KeyStageId != null ? String(ks.e180_KeyStageId).trim() : '';
      const years = ks.e180_Years ?? [];
      let keyStageExpanded = false;

      for (let yi = 0; yi < years.length; yi++) {
        const year = years[yi];
        const yId = year.e180_YearId != null ? String(year.e180_YearId).trim() : '';
        if (!ksId || !yId) continue;

        const matched = placements.some((p) => {
          if (placementMatchKey(p.keyStageId, p.yearId) !== placementMatchKey(ksId, yId)) {
            return false;
          }
          const wantSubject = normalizeSubjectNameForCompare(p.subjectName);
          if (!wantSubject) return true;
          return wantSubject === subjectLabel;
        });
        if (!matched) continue;

        subjectExpanded = true;
        keyStageExpanded = true;
      }

      if (keyStageExpanded) {
        expand.add(`${branchId}-ks-${ki}`);
      }
    }

    if (subjectExpanded) {
      expand.add(branchId);
    }
  }
  return expand;
}

export function studentSubjectBranchIdFromE180(subject: { e180_SubjectId?: string | null }): string {
    const sid = subject.e180_SubjectId != null ? String(subject.e180_SubjectId).trim() : '';
    if (!sid) {
        throw new Error('e180_SubjectId is required for student curriculum tree nodes');
    }
    return `s-${sid}`;
}

/** Canonical year node id for student curriculum lazy-load (C287 / E273). Matches tree + server parser. */
export function studentCurriculumYearNodeId(
    subject: { e180_SubjectId?: string | null },
    keyStageIndex: number,
    yearIndex: number
): string {
    return `${studentSubjectBranchIdFromE180(subject)}-y-${keyStageIndex}-${yearIndex}`;
}

export function findStudentCurriculumBranchForNodeId(
    subjects: any[] | undefined,
    nodeId: string
): { subject: any; branchId: string } | null {
    if (!subjects?.length || !nodeId) return null;
    let best: { subject: any; branchId: string } | null = null;
    for (const s of subjects) {
        let bid: string;
        try {
            bid = studentSubjectBranchIdFromE180(s);
        } catch {
            continue;
        }
        if (nodeId === bid || nodeId.startsWith(`${bid}-`)) {
            if (!best || bid.length > best.branchId.length) {
                best = { subject: s, branchId: bid };
            }
        }
    }
    return best;
}

export type ParsedLibraryE271MergeTarget =
    | { kind: 'year'; subject: any; keystageIndex: number; yearIndex: number }
    | { kind: 'topic'; subject: any; keystageIndex: number; yearIndex: number; topicIndices: number[] };

/** Resolve lazy-load node id from CurriculumLibraryTreeView for merging E271 into E179. */
export function parseLibraryE271MergeTarget(
    subjects: any[],
    nodeId: string,
    nodeType: string
): ParsedLibraryE271MergeTarget | null {
    if (!subjects?.length || !nodeId) return null;
    const nt = (nodeType || '').toLowerCase();
    let best: { subject: any; branchId: string } | null = null;
    for (const s of subjects) {
        const bid = librarySubjectBranchIdFromE179(s);
        if (nodeId === bid || nodeId.startsWith(`${bid}-`)) {
            if (!best || bid.length > best.branchId.length) {
                best = { subject: s, branchId: bid };
            }
        }
    }
    if (!best) return null;
    const rest = nodeId.slice(best.branchId.length + 1);
    if (!rest) return null;

    if (nt === 'year') {
        const m = /^y-(\d+)-(\d+)$/.exec(rest);
        if (!m) return null;
        return {
            kind: 'year',
            subject: best.subject,
            keystageIndex: parseInt(m[1], 10),
            yearIndex: parseInt(m[2], 10)
        };
    }
    if (nt === 'topic') {
        const segs = rest.split('-');
        if (segs.length < 3) return null;
        const nums = segs.map(x => parseInt(x, 10));
        if (nums.some(n => Number.isNaN(n))) return null;
        const keystageIndex = nums[0];
        const yearIndex = nums[1];
        const topicIndices = nums.slice(2);
        if (topicIndices.length === 0) return null;
        return { kind: 'topic', subject: best.subject, keystageIndex, yearIndex, topicIndices };
    }
    return null;
}

/**
 * Parse student-curriculum lazy-load node ids (same shape as library: s-{subjectEntityId}-ks-… / -y-… / topic suffixes).
 */
export function parseStudentCurriculumLazyMergeTarget(
    subjects: any[],
    nodeId: string,
    nodeType: string
): ParsedLibraryE271MergeTarget | null {
    const best = findStudentCurriculumBranchForNodeId(subjects, nodeId);
    if (!best) return null;
    const nt = (nodeType || '').toLowerCase();
    const rest = nodeId.slice(best.branchId.length + 1);
    if (!rest) return null;

    if (nt === 'year') {
        const m = /^y-(\d+)-(\d+)$/.exec(rest);
        if (!m) return null;
        return {
            kind: 'year',
            subject: best.subject,
            keystageIndex: parseInt(m[1], 10),
            yearIndex: parseInt(m[2], 10)
        };
    }
    if (nt === 'topic') {
        const segs = rest.split('-');
        if (segs.length < 3) return null;
        const nums = segs.map((x) => parseInt(x, 10));
        if (nums.some((n) => Number.isNaN(n))) return null;
        const keystageIndex = nums[0];
        const yearIndex = nums[1];
        const topicIndices = nums.slice(2);
        if (topicIndices.length === 0) return null;
        return { kind: 'topic', subject: best.subject, keystageIndex, yearIndex, topicIndices };
    }
    return null;
}

/** Resolve year row from student tree year node id (droppable for reorder). */
export function parseStudentCurriculumYearFromNodeId(
    subjects: any[] | undefined,
    yearNodeId: string
): { subject: any; keystageIndex: number; yearIndex: number } | null {
    if (!subjects?.length || !yearNodeId) return null;
    const best = findStudentCurriculumBranchForNodeId(subjects, yearNodeId);
    if (!best) return null;
    const rest = yearNodeId.slice(best.branchId.length + 1);
    const m = /^y-(\d+)-(\d+)$/.exec(rest);
    if (!m) return null;
    return {
        subject: best.subject,
        keystageIndex: parseInt(m[1], 10),
        yearIndex: parseInt(m[2], 10)
    };
}

/** Replace a year's flat topic list after reorder (optimistic E180 patch). */
export function applyReorderedYearTopics(
    studentCurriculumData: { e180_Subjects?: any[] } | null | undefined,
    yearNodeId: string,
    reorderedTopics: any[]
): { e180_Subjects?: any[] } | null {
    if (!studentCurriculumData?.e180_Subjects) return studentCurriculumData ?? null;

    const parsed = parseStudentCurriculumYearFromNodeId(studentCurriculumData.e180_Subjects, yearNodeId);
    if (!parsed) return studentCurriculumData;

    const { subject, keystageIndex, yearIndex } = parsed;
    const subjectIndex = studentCurriculumData.e180_Subjects.indexOf(subject);
    if (subjectIndex < 0) return studentCurriculumData;

    const subjects = studentCurriculumData.e180_Subjects.map((s, si) => {
        if (si !== subjectIndex) return s;
        return {
            ...s,
            e180_KeyStages: s.e180_KeyStages?.map((ks: any, ki: number) => {
                if (ki !== keystageIndex) return ks;
                return {
                    ...ks,
                    e180_Years: ks.e180_Years?.map((y: any, yi: number) => {
                        if (yi !== yearIndex) return y;
                        return { ...y, e180_Topics: reorderedTopics };
                    })
                };
            })
        };
    });

    return { ...studentCurriculumData, e180_Subjects: subjects };
}

/** Optimistic reorder: move one topic row within a year's e180_Topics (droppable index matches tree child order). */
export function reorderStudentCurriculumYearTopics(
    studentCurriculumData: { e180_Subjects?: any[] } | null | undefined,
    yearNodeId: string,
    sourceIndex: number,
    destinationIndex: number
): { e180_Subjects?: any[] } | null {
    if (!studentCurriculumData?.e180_Subjects) return studentCurriculumData ?? null;

    const parsed = parseStudentCurriculumYearFromNodeId(studentCurriculumData.e180_Subjects, yearNodeId);
    if (!parsed) return studentCurriculumData;

    const { subject, keystageIndex, yearIndex } = parsed;
    const subjectIndex = studentCurriculumData.e180_Subjects.indexOf(subject);
    if (subjectIndex < 0) return studentCurriculumData;

    const keyStage = subject.e180_KeyStages?.[keystageIndex];
    const year = keyStage?.e180_Years?.[yearIndex];
    if (!year?.e180_Topics?.length) return studentCurriculumData;

    const topics = [...year.e180_Topics];
    if (
        sourceIndex < 0 ||
        destinationIndex < 0 ||
        sourceIndex >= topics.length ||
        destinationIndex >= topics.length
    ) {
        return studentCurriculumData;
    }

    const [moved] = topics.splice(sourceIndex, 1);
    topics.splice(destinationIndex, 0, moved);

    const subjects = studentCurriculumData.e180_Subjects.map((s, si) => {
        if (si !== subjectIndex) return s;
        return {
            ...s,
            e180_KeyStages: s.e180_KeyStages?.map((ks: any, ki: number) => {
                if (ki !== keystageIndex) return ks;
                return {
                    ...ks,
                    e180_Years: ks.e180_Years?.map((y: any, yi: number) => {
                        if (yi !== yearIndex) return y;
                        return { ...y, e180_Topics: topics };
                    })
                };
            })
        };
    });

    return { ...studentCurriculumData, e180_Subjects: subjects };
}

export function mapE271TopicRowToE179(topic: any): any {
    const childSource = topic?.e271_ChildTopics ?? topic?.E271_ChildTopics ?? [];
    return {
        e179_Name: topic?.e271_Name ?? topic?.E271_Name,
        e179_TopicId: topic?.e271_TopicId ?? topic?.E271_TopicId,
        e179_Details: topic?.e271_Details ?? topic?.E271_Details,
        e179_MinHours: topic?.e271_MinHours ?? topic?.E271_MinHours,
        e179_IsInStudentCurriculum: topic?.e271_IsInStudentCurriculum ?? topic?.E271_IsInStudentCurriculum,
        e179_TotalChildTopicCount: topic?.e271_TotalChildTopicCount ?? topic?.E271_TotalChildTopicCount ?? 0,
        e179_ChildTopics: (childSource || []).map((c: any) => mapE271TopicRowToE179(c))
    };
}

const findNestedTopicLocation = (
    parentTopic: any,
    topicId: string,
    subjectId: string | null,
    subjectName: string,
    keyStageName: string,
    keyStageId: string | null,
    yearLabel: string,
    yearId: string | null,
    educationSystemKey: string | null,
    isE180: boolean = false
): TopicMetadata | null => {
    const topicsField = isE180 ? 'e180_Topics' : 'e179_Topics';
    const childTopicsField = isE180 ? 'e180_ChildTopics' : 'e179_ChildTopics';
    const topicIdField = isE180 ? 'e180_TopicId' : 'e179_TopicId';
    
    const childTopics = parentTopic[topicsField] || parentTopic[childTopicsField];
    if (childTopics) {
        for (const topic of childTopics) {
            if (topic[topicIdField] === topicId) {
                return {
                    subjectId,
                    subjectName,
                    keyStageName,
                    keyStageId,
                    yearLabel,
                    yearId,
                    educationSystemKey
                };
            }
            const deeperResult = findNestedTopicLocation(
                topic,
                topicId,
                subjectId,
                subjectName,
                keyStageName,
                keyStageId,
                yearLabel,
                yearId,
                educationSystemKey,
                isE180
            );
            if (deeperResult) return deeperResult;
        }
    }
    return null;
};

const findTopicLocation = (subjects: any[], topicId: string, isE180: boolean = false): TopicMetadata => {
    const subjectField = isE180 ? 'e180_Subjects' : 'e179_Subjects';
    const keyStagesField = isE180 ? 'e180_KeyStages' : 'e179_KeyStages';
    const yearsField = isE180 ? 'e180_Years' : 'e179_Years';
    const topicsField = isE180 ? 'e180_Topics' : 'e179_Topics';
    const childTopicsField = isE180 ? 'e180_ChildTopics' : 'e179_ChildTopics';
    const nameField = isE180 ? 'e180_Name' : 'e179_Name';
    const topicIdField = isE180 ? 'e180_TopicId' : 'e179_TopicId';
    const ksIdField = isE180 ? 'e180_KeyStageId' : 'e179_KeyStageId';
    const yearIdField = isE180 ? 'e180_YearId' : 'e179_YearId';
    const subjectRowIdField = isE180 ? 'e180_SubjectId' : 'e179_SubjectId';

    for (const subject of subjects) {
        const subjectRowId = yearIdString(subject[subjectRowIdField]);
        const rawEs = isE180
            ? (subject.e180_EducationSystemKey as string | undefined)
            : (subject.e179_EducationSystemKey as string | undefined);
        const subjectEs: string | null = rawEs?.trim() || null;
        const keyStages = subject[keyStagesField] || [];
        for (const keyStage of keyStages) {
            const ksId = yearIdString(keyStage[ksIdField]);
            const years = keyStage[yearsField] || [];
            for (const year of years) {
                const yId = yearIdString(year[yearIdField]);
                const yLabel =
                    isE180
                        ? (year.e180_YearLabel as string) || ''
                        : libraryYearDisplayName(year);
                const topics = year[topicsField] || [];
                for (const topic of topics) {
                    if (topic[topicIdField] === topicId) {
                        return {
                            subjectId: subjectRowId,
                            subjectName: subject[nameField],
                            keyStageName: keyStage[nameField],
                            keyStageId: ksId,
                            yearLabel: yLabel || null,
                            yearId: yId,
                            educationSystemKey: subjectEs
                        };
                    }
                    // Check nested topics
                    const childTopics = topic[topicsField] || topic[childTopicsField];
                    if (childTopics) {
                        const nestedResult = findNestedTopicLocation(
                            topic,
                            topicId,
                            subjectRowId,
                            subject[nameField],
                            keyStage[nameField],
                            ksId,
                            yLabel,
                            yId,
                            subjectEs,
                            isE180
                        );
                        if (nestedResult) return nestedResult;
                    }
                }
            }
        }
    }
    return {
        subjectId: null,
        subjectName: null,
        keyStageName: null,
        keyStageId: null,
        yearLabel: null,
        yearId: null,
        educationSystemKey: null
    };
};

/** Distinct E180 student subject line ids for the given selected topics. */
export function collectStudentSubjectIdsForSelectedTopics(
    selectedTopics: any[],
    studentCurriculumData: any,
    extractMetadata: (topic: any, curriculum: any) => TopicMetadata
): string[] {
    const ids = new Set<string>();
    for (const topic of selectedTopics ?? []) {
        const subjectId = extractMetadata(topic, studentCurriculumData).subjectId;
        if (subjectId?.trim()) {
            ids.add(subjectId.trim());
        }
    }
    return [...ids];
}

export const extractTopicMetadata = (topicData: any, curriculumData: any): TopicMetadata => {
    // First check if metadata was already stored with the topic
    if (topicData?._metadata) {
        const m = topicData._metadata as Partial<TopicMetadata>;
        return {
            subjectId: m.subjectId ?? null,
            subjectName: m.subjectName ?? null,
            keyStageName: m.keyStageName ?? null,
            keyStageId: m.keyStageId ?? null,
            yearId: m.yearId ?? null,
            yearLabel: m.yearLabel ?? null,
            educationSystemKey: m.educationSystemKey ?? null
        };
    }

    // Handle both e179 (provider) and e180 (student curriculum) structures
    const topicId = topicData?.e179_TopicId || topicData?.e180_TopicId;
    if (!curriculumData || !topicId) {
        return {
            subjectId: null,
            subjectName: null,
            keyStageName: null,
            keyStageId: null,
            yearLabel: null,
            yearId: null,
            educationSystemKey: null
        };
    }

    // Check for e179 structure (provider curriculum)
    if (curriculumData.e179_Subjects) {
        return findTopicLocation(curriculumData.e179_Subjects, topicId, false);
    }
    
    // Check for e180 structure (student curriculum)
    if (curriculumData.e180_Subjects) {
        return findTopicLocation(curriculumData.e180_Subjects, topicId, true);
    }

    return {
        subjectId: null,
        subjectName: null,
        keyStageName: null,
        keyStageId: null,
        yearLabel: null,
        yearId: null,
        educationSystemKey: null
    };
};

const findNestedTopicPath = (parentTopic: any, topicId: string, currentPath: string[]): string[] | null => {
    // Check both e179_Topics and e179_ChildTopics for nested topics
    const childTopics = parentTopic.e179_Topics || parentTopic.e179_ChildTopics;
    if (childTopics) {
        for (const topic of childTopics) {
            if (topic.e179_TopicId === topicId) {
                return [...currentPath, topic.e179_Name];
            }
            const deeperPath = findNestedTopicPath(topic, topicId, [...currentPath, topic.e179_Name]);
            if (deeperPath) return deeperPath;
        }
    }
    return null;
};

const findTopicPath = (subjects: any[], topicId: string): string[] | null => {
    for (const subject of subjects) {
        for (const keyStage of subject.e179_KeyStages) {
            for (const year of keyStage.e179_Years) {
                const yLabel = libraryYearDisplayName(year);
                for (const topic of year.e179_Topics) {
                    if (topic.e179_TopicId === topicId) {
                        return [subject.e179_Name, keyStage.e179_Name, yLabel, topic.e179_Name];
                    }
                    // Check both e179_Topics and e179_ChildTopics for nested topics
                    const childTopics = topic.e179_Topics || topic.e179_ChildTopics;
                    if (childTopics) {
                        const nestedPath = findNestedTopicPath(topic, topicId, [subject.e179_Name, keyStage.e179_Name, yLabel, topic.e179_Name]);
                        if (nestedPath) return nestedPath;
                    }
                }
            }
        }
    }
    return null;
};

/** Map E308 tenant custom curriculum event to the same shape as E179 for CurriculumLibraryTreeView. */
export function mapE308TenantCurriculumToLibraryShape(event: any) {
    if (!event?.e308_Subjects) {
        return { e179_Subjects: [] };
    }
    return {
        e179_Subjects: event.e308_Subjects.map((s: any) => ({
            e179_Name: s.e308_Name,
            e179_SubjectId: s.e308_SubjectId,
            e179_EducationSystemKey: s.e308_EducationSystemKey,
            e179_KeyStages: (s.e308_KeyStages || []).map((ks: any) => ({
                e179_Name: ks.e308_Name,
                e179_KeyStageId: ks.e308_KeyStageId,
                e179_Years: (ks.e308_Years || []).map((y: any) => ({
                    e179_Number: null,
                    e179_Name: y.e308_YearLabel,
                    e179_YearId: y.e308_YearId ?? y.E308_YearId,
                    e179_TotalTopicCount: y.e308_TotalTopicCount,
                    e179_Topics: (y.e308_Topics || []).map(mapE308TopicToE179)
                }))
            }))
        }))
    };
}

/** Rows for Custom Topic / Custom Topics subject dropdown (tenant catalog only; E308 mapped library shape). */
export type TenantCustomCatalogSubjectRow = {
    catalogSubjectId: string;
    name: string;
    educationSystemKey: string;
};

export function tenantCustomCatalogSubjectRowsFromLibrary(
    tenantCustomCurriculumLibraryData: any | null | undefined
): TenantCustomCatalogSubjectRow[] {
    const subjects = tenantCustomCurriculumLibraryData?.e179_Subjects;
    if (!Array.isArray(subjects)) return [];
    const rows: TenantCustomCatalogSubjectRow[] = [];
    for (const s of subjects) {
        const id = String(s?.e179_SubjectId ?? s?.E179_SubjectId ?? '').trim();
        const name = String(s?.e179_Name ?? s?.E179_Name ?? '').trim();
        const educationSystemKey = String(s?.e179_EducationSystemKey ?? s?.E179_EducationSystemKey ?? '').trim();
        if (!id || !name) continue;
        rows.push({ catalogSubjectId: id, name, educationSystemKey });
    }
    rows.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return rows;
}

function mapE308TopicToE179(t: any): any {
    return {
        e179_Name: t.e308_Name,
        e179_Details: t.e308_Details,
        e179_TopicId: t.e308_TopicId,
        e179_MinHours: t.e308_MinHours,
        e179_PriorKnowledgeRequired: t.e308_PriorKnowledgeRequired || [],
        e179_NationalCurriculumContent: t.e308_NationalCurriculumContent || [],
        e179_Keywords: (t.e308_Keywords || []).map((k: any) => ({
            e179_Keyword: k.e308_Keyword,
            e179_Description: k.e308_Description
        })),
        e179_LearningObjectives: t.e308_LearningObjectives || [],
        e179_SupervisionLevel: t.e308_SupervisionLevel,
        e179_Outcome: t.e308_Outcome,
        e179_TeacherTip: t.e308_TeacherTip,
        e179_Misconceptions: t.e308_Misconceptions,
        e179_ProviderKey: t.e308_ProviderKey || 'custom',
        e179_ProviderTopicId: t.e308_ProviderTopicId,
        e179_ChildTopics: []
    };
}

export const buildBreadcrumb = (topicData: any, curriculumData: any): string => {
    // First check if metadata was already stored with the topic
    if (topicData?._metadata) {
        const metadata = topicData._metadata;
        const path = [
            metadata.subjectName,
            metadata.keyStageName,
            metadata.yearLabel || null,
            topicData.e179_Name
        ].filter(Boolean);
        return path.join(' > ');
    }

    if (!curriculumData || !topicData?.e179_TopicId) {
        return messages.breadcrumbLoading;
    }

    const path = findTopicPath(curriculumData.e179_Subjects, topicData.e179_TopicId);
    return path ? path.join(' > ') : messages.breadcrumbUnknownPath;
};

/**
 * True when the tenant has at least one custom-authored subject in the student curriculum (E180) or in the
 * tenant custom catalog (E308 mapped to library shape with e179_Subjects).
 */
export function hasCustomAuthoredSubject(
    studentCurriculumData: any,
    tenantCustomCurriculumLibraryData?: any | null
): boolean {
    const subjects = studentCurriculumData?.e180_Subjects;
    if (Array.isArray(subjects)) {
        const fromMy = subjects.some((s: any) => {
            const key = s?.e180_EducationSystemKey ?? s?.E180_EducationSystemKey;
            const dn = s?.e180_EducationSystemDisplayName ?? s?.E180_EducationSystemDisplayName;
            return (
                (typeof key === 'string' && key.trim().length > 0) ||
                (typeof dn === 'string' && dn.trim().length > 0)
            );
        });
        if (fromMy) return true;
    }
    const catSubjects = tenantCustomCurriculumLibraryData?.e179_Subjects;
    return Array.isArray(catSubjects) && catSubjects.length > 0;
}
